import {chain, xor} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {collectEnumValues} from '../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {SettingsInboxesNewCreatorState} from '../../../../settings/inboxes/newCreator/settingsInboxesNewCreatorState';
import {OnboardingSelectionButton} from '../onboardingSelectionButton';
import {
  StyledFormDescriptionDiv,
  StyledOnboardingFormTitleDiv,
  StyledOnboardingFormTitlesDiv,
} from '../onboardingWelcomeHelpers';
import {OnboardingWelcomeSlide, OnboardingWelcomeSlideProps} from '../onboardingWelcomeSlide';
import {
  OnboardingCarouselTagTemplate,
  onboardingCarouselTagTemplates,
  OnboardingCarouselWorkflowTemplate,
  onboardingCarouselWorkflowTemplates,
} from './onboardingCarouselResourceTemplates';
import {
  onboardingWorkflowsOutcomesConstantsV2,
  OnboardingWorkflowsOutcomesV2,
} from './onboardingWorkflowsOutcomesV2';

/*
 * Props.
 */

export interface OnboardingWorkflowsOutcomeSlideProps
  extends Pick<OnboardingWelcomeSlideProps, 'navPrimary' | 'navSecondary'> {
  selectedOutcomes: ReadonlyArray<OnboardingWorkflowsOutcomesV2>;
  onChangeOutcomes: (outcomes: ReadonlyArray<OnboardingWorkflowsOutcomesV2>) => void;
  inboxState: SettingsInboxesNewCreatorState;
  companyName: string;
}

/*
 * Styles.
 */

const StyledHalfContainer = styled.div`
  flex: 0 0 50%;
`;

const StyledSelectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  > * {
    width: fit-content;
  }
`;

/*
 * Component.
 */

export const OnboardingWorkflowsOutcomeSlide: FC<OnboardingWorkflowsOutcomeSlideProps> = ({
  selectedOutcomes,
  onChangeOutcomes,
  inboxState,
  companyName,
  ...slideProps
}) => {
  const outcomeConstants = onboardingWorkflowsOutcomesConstantsV2;

  const toggleOutcome = (outcome: OnboardingWorkflowsOutcomesV2) => {
    const isNotCurrentlySelected = !selectedOutcomes.includes(outcome);
    if (isNotCurrentlySelected) {
      animateOutcomeNodes(outcome);
    }

    return onChangeOutcomes(xor(selectedOutcomes, [outcome]));
  };

  return (
    <OnboardingWelcomeSlide {...slideProps} isLeftContent>
      <StyledHalfContainer>
        <StyledOnboardingFormTitlesDiv>
          <StyledOnboardingFormTitleDiv>
            <Trans id="onboardingwelcome-workflows-title-v3">Specify your goals for this inbox</Trans>
          </StyledOnboardingFormTitleDiv>
        </StyledOnboardingFormTitlesDiv>
        <StyledFormDescriptionDiv>
          <Trans id="onboardingwelcome-workflows-description-v3">
            We&rsquo;ll help you set up a few basic rules to achieve these goals.
          </Trans>
        </StyledFormDescriptionDiv>

        <StyledSelectionWrapper>
          {collectEnumValues(OnboardingWorkflowsOutcomesV2).map((outcome) => (
            <OnboardingSelectionButton
              key={outcome}
              type="checkbox"
              isChecked={selectedOutcomes.includes(outcome)}
              onClick={() => toggleOutcome(outcome)}
              label={outcomeConstants[outcome].label}
            />
          ))}
        </StyledSelectionWrapper>
      </StyledHalfContainer>
    </OnboardingWelcomeSlide>
  );
};

/*
 * Helpers.
 */

interface OutcomeResources {
  workflowsTemplates: ReadonlyArray<{id: string; template: OnboardingCarouselWorkflowTemplate}>;
  tagsTemplates: ReadonlyArray<{id: string; template: OnboardingCarouselTagTemplate}>;
  hasBusinessHours: boolean;
}

function getOutcomeResources(outcome: OnboardingWorkflowsOutcomesV2): OutcomeResources {
  const outcomeConstants = onboardingWorkflowsOutcomesConstantsV2;
  const workflowTemplateIds = outcomeConstants[outcome].templates;
  const workflowsTemplates = workflowTemplateIds.map((id) => ({
    id,
    template: onboardingCarouselWorkflowTemplates[id],
  }));

  const tagsTemplates = chain(workflowsTemplates)
    .flatMap(({template}) => ('tags' in template ? template.tags : []))
    .uniq()
    .map((id) => ({id, template: onboardingCarouselTagTemplates[id]}))
    .value();

  const hasBusinessHours = workflowsTemplates.some(
    ({template}) => 'hasBusinessHours' in template && template.hasBusinessHours,
  );

  return {
    workflowsTemplates,
    tagsTemplates,
    hasBusinessHours,
  };
}

// Start a bounce animation on currently-visible node that are in the provided outcome.
function animateOutcomeNodes(outcome: OnboardingWorkflowsOutcomesV2) {
  const {workflowsTemplates, tagsTemplates, hasBusinessHours} = getOutcomeResources(outcome);

  const workflowNodes = workflowsTemplates.flatMap(({id}) =>
    Array.from(document.querySelectorAll(`[data-front-rule-template-id="${id}"]`)),
  );
  const tagNodes = tagsTemplates.flatMap(({id}) =>
    Array.from(document.querySelectorAll(`[data-front-tag-template-id="${id}"]`)),
  );
  const businessHoursNode = hasBusinessHours
    ? document.querySelector('[data-front-business-hours-illustration]')
    : undefined;

  for (const node of [...workflowNodes, ...tagNodes, businessHoursNode]) {
    node?.animate([{scale: '1.05'}, {scale: '1'}], {duration: 150});
  }
}
